'use client';

import React from 'react';
import { Pencil, Trash2, RefreshCw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { ScheduledPost } from './ContentCalendarContent';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import { CalendarDays } from 'lucide-react';

interface Props {
  posts: ScheduledPost[];
}

const approvalStyle: Record<string, string> = {
  approved: 'text-positive bg-positive/10',
  pending: 'text-warning bg-warning/10',
  rejected: 'text-negative bg-negative/10',
};

export default function PostListView({ posts }: Props) {
  if (posts.length === 0) {
    return (
      <div className="card-base">
        <EmptyState
          icon={CalendarDays}
          title="No posts scheduled"
          description="Schedule your first post by clicking 'Schedule Post' to start building your content calendar."
          action={{ label: 'Schedule Post', onClick: () => {} }}
        />
      </div>
    );
  }

  return (
    <div className="card-base overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Post</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client / Page</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Platform</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Scheduled</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Approval</th>
              <th className="px-4 py-3 w-10"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {posts.map((post) => (
              <tr key={post.id} className="table-row-hover group">
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-foreground max-w-[220px] truncate">{post.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 max-w-[220px] truncate">{post.copy}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-foreground">{post.client}</p>
                  <p className="text-xs text-muted-foreground">{post.page}</p>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={post.platform}>{post.platform === 'facebook' ? 'Facebook' : 'Instagram'}</Badge>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{post.type}</span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs text-foreground font-medium">{post.scheduledDate}</p>
                  <p className="text-xs text-muted-foreground">{post.scheduledTime}</p>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={post.status} dot>{post.status.charAt(0).toUpperCase() + post.status.slice(1)}</Badge>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${approvalStyle[post.approvalStatus]}`}>
                    {post.approvalStatus.charAt(0).toUpperCase() + post.approvalStatus.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button onClick={() => toast.info(`Viewing "${post.title}"`)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all" title="Preview post">
                      <Eye size={12} />
                    </button>
                    <button onClick={() => toast.info(`Editing "${post.title}"`)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all" title="Edit post">
                      <Pencil size={12} />
                    </button>
                    {post.status === 'failed' && (
                      <button onClick={() => toast.success(`Retrying publish for "${post.title}"`)} className="w-7 h-7 rounded-lg flex items-center justify-center text-warning hover:bg-warning/10 transition-all" title="Retry publish">
                        <RefreshCw size={12} />
                      </button>
                    )}
                    <button onClick={() => toast.error(`Delete "${post.title}"? This cannot be undone.`)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-negative/10 hover:text-negative transition-all" title="Delete post">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}